import { useMemo, type CSSProperties } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ButtonElement from '@/components/elements/Button/Button';
import PageNotFound from '@/components/elements/PageNotFound/PageNotFound';
import TitleWithContent from '@/components/elements/TitleWithContent/TitleWithContent';
import { useSeo } from '@/seo/useSeo';
import { galleryCategorySeo } from '@/seo/detailSeo';
import { useGalleryMedia } from './useGalleryMedia';
import GalleryGrid from './GalleryGrid';

/** Matches TitleWithContent's own (inline, clamp-based) padding — see Gallery.tsx. */
const CONTENT_PADDING_STYLE: CSSProperties = {
  paddingLeft: 'clamp(.2rem, 6vw, 8rem)',
  paddingRight: 'clamp(.2rem, 6vw, 8rem)',
};

/**
 * One page per gallery category (`/gallery/<slug>`), plus the special slug "all" for the
 * unfiltered library. Reached from a real `<Link>` on the landing page (see Gallery.tsx), so
 * it's a real URL: shareable, bookmarkable, and crawlable.
 *
 * A category is "real" if either a Sanity "Gallery Category" document has this slug (fetched
 * separately from items, so a category that exists but has nothing tagged yet still gets a
 * proper page instead of a 404), or a local item is currently tagged with it. Anything else is
 * a genuinely bad URL.
 */
const GalleryCategoryPage = () => {
  const { category: slug } = useParams<{ category: string }>();
  const isAll = slug === 'all';
  const navigate = useNavigate();

  const { mediaArray, categories, status } = useGalleryMedia();

  const items = useMemo(
    () => (isAll ? mediaArray : mediaArray.filter((item) => item.category?.slug === slug)),
    [mediaArray, isAll, slug]
  );

  const knownCategory = isAll
    ? null
    : (categories.find((candidate) => candidate.slug === slug) ?? items[0]?.category ?? null);
  const category = isAll ? null : knownCategory;
  // Still loading: neither the category list nor any matching item has arrived yet, so we
  // can't yet tell "doesn't exist" from "exists, just hasn't loaded" — wait rather than 404.
  const stillResolving = !isAll && !category && status === 'loading';

  // "all" is fixed and already handled statically (see routeMeta.ts); a real category's tags
  // are only known once its data has arrived — null is a no-op, so this never disturbs that.
  useSeo(isAll || !category ? null : galleryCategorySeo({ name: category.title, path: `/gallery/${slug}` }));

  // Still fetching, so we can't yet tell "unknown category" from "not loaded yet".
  if (stillResolving) {
    return <p className="mt-[5rem] min-h-screen pt-24 text-center text-gray-500">Loading…</p>;
  }

  // Not "all" and not a real category slug: a genuine bad URL, not missing content.
  if (!isAll && !category) {
    return <PageNotFound />;
  }

  const title = isAll ? 'All Photos & Videos' : category!.title;

  return (
    // pt-24 on top of the mt-[5rem] margin (same as the loading/not-found states above) clears
    // the fixed navbar: its element is much taller than what's visible (it carries bottom
    // padding for its own layout), so anything placed right at the top of the page — like the
    // back button — would otherwise sit underneath it and silently eat its clicks.
    <div className="2xl:px-40 xl:px-35 lg:px-30 md:px-20 px-3 mt-[5rem] pt-24 mb-[5rem]">
      <div style={CONTENT_PADDING_STYLE} className="flex justify-end">
        <ButtonElement btnText="Back to Gallery" size="small" onClick={() => navigate('/gallery')} />
      </div>

      <TitleWithContent title={title} level={1} />

      <div style={CONTENT_PADDING_STYLE}>
        {status === 'error' ? (
          <p className="mt-6 text-gray-500">
            We couldn&apos;t load the gallery right now. Please try again later.
          </p>
        ) : (
          <GalleryGrid
            items={items}
            emptyMessage={
              isAll
                ? 'No photos or videos yet — check back soon.'
                : `No photos have been tagged under ${title} yet — check back soon, or browse all photos & videos.`
            }
          />
        )}
      </div>
    </div>
  );
};

export default GalleryCategoryPage;
